import * as z from "zod";

export const signupSchema = z.object({
  email: z.email(),
  password: z.string().min(6, {
    error: "Password must be at least 6 characters",
  }),
  fullName: z.string().min(1, {
    error: "Full name is required",
  }),
  recaptchaToken: z.string().optional(),
});

export const signinSchema = z.object({
  email: z.email(),
  password: z.string().min(1, {
    error: "Password is required",
  }),
  recaptchaToken: z.string().optional(),
});

export const refreshSchema = z.object({
  refreshToken: z.string().min(1, {
    error: "Refresh token is required",
  }),
});

export const passwordResetSendSchema = z.object({
  email: z.email(),
  // Optional locale to allow localized emails (e.g., "en", "fr", etc.)
  locale: z.string().optional(),
  recaptchaToken: z.string().optional(),
});

export const emailVerifySendSchema = z.object({
  idToken: z.string().min(1, {
    error: "ID token is required",
  }),
  locale: z.string().optional(),
});

export const emailVerifyConfirmSchema = z.object({
  oobCode: z.string().min(1, {
    error: "Verification code is required",
  }),
});

// User Profile Schemas for @handle functionality
export const handleSchema = z
  .string()
  .min(3, "Handle must be at least 3 characters")
  .max(30, "Handle must be less than 30 characters")
  .regex(
    /^[a-zA-Z0-9_]+$/,
    "Handle can only contain letters, numbers, and underscores"
  )
  .refine((s) => !s.startsWith("_"), "Handle cannot start with underscore");

// Profile Image Schemas
export const profileImageVariantSchema = z.object({
  url: z.url(),
  dimensions: z.object({
    width: z.number().int().positive(),
    height: z.number().int().positive(),
  }),
});

export const profileImagesSchema = z.object({
  original: profileImageVariantSchema,
  large: profileImageVariantSchema,
  medium: profileImageVariantSchema,
  small: profileImageVariantSchema,
  thumb: profileImageVariantSchema,
});

export const createUserProfileSchema = z.object({
  handle: handleSchema,
  display_name: z.string().max(100).optional(),
  bio: z.string().max(500).optional(),
  avatar_url: z.url().optional(), // Legacy field
  profile_images: profileImagesSchema.optional(),
  grade_preference: z.enum(["v-scale", "font"]).optional(),
  unit_preference: z.enum(["metric", "imperial"]).optional(),
});

export const updateUserProfileSchema = createUserProfileSchema.partial();

export const userSearchSchema = z.object({
  query: z.string().min(1, "Search query is required"),
  limit: z.coerce.number().int().positive().max(50).default(20),
  offset: z.coerce.number().int().min(0).default(0),
});

export const userRoleSchema = z.enum(["moderator", "curator", "editor"]);
export const permissionTypeSchema = z.enum(["admin", "editor"]);
export const contentTypeSchema = z.enum(["areas", "models", "routes"]);

export const addPermissionSchema = z.object({
  userId: z.string().min(1, "User ID is required"),
  permissionType: permissionTypeSchema,
});

export const removePermissionSchema = z.object({
  userId: z.string().min(1, "User ID is required"),
  permissionType: permissionTypeSchema.optional(),
});

export const updatePermissionSchema = z.object({
  userId: z.string().min(1, "User ID is required"),
  oldPermissionType: permissionTypeSchema,
  newPermissionType: permissionTypeSchema,
});

export const userRoleSchema_record = z.object({
  id: z.uuid(),
  user_id: z.string(),
  role_type: userRoleSchema,
  created_at: z.iso.datetime(),
  updated_at: z.iso.datetime(),
  created_by: z.string().nullable(),
});

export const contentPermissionSchema = z.object({
  id: z.uuid(),
  user_id: z.string(),
  content_type: contentTypeSchema,
  content_id: z.uuid(),
  permission_type: permissionTypeSchema,
  created_at: z.iso.datetime(),
  created_by: z.string().nullable(),
});

// Bookmark schemas
export const bookmarkEntityTypeSchema = z.enum(["area", "model", "route"]);

export const userBookmarkSchema = z.object({
  id: z.uuid().describe("Unique identifier for the bookmark"),
  user_id: z.string().describe("Firebase user ID who owns this bookmark"),
  entity_type: bookmarkEntityTypeSchema.describe(
    "Type of climbing entity being bookmarked"
  ),
  entity_id: z.uuid().describe("ID of the climbing entity being bookmarked"),
  created_at: z.iso.datetime().describe("Timestamp when bookmark was created"),
});

export const createBookmarkSchema = z.object({
  entity_type: bookmarkEntityTypeSchema,
  entity_id: z.uuid(),
});

export const deleteBookmarkSchema = z.object({
  entity_type: bookmarkEntityTypeSchema,
  entity_id: z.uuid(),
});

export const getUserBookmarksSchema = z.object({
  entity_type: bookmarkEntityTypeSchema
    .optional()
    .describe("Filter by entity type"),
  limit: z.coerce.number().int().positive().max(100).default(50),
  offset: z.coerce.number().int().min(0).default(0),
});

export const bulkBookmarkStatusSchema = z.object({
  bookmarks: z
    .array(
      z.object({
        entity_type: bookmarkEntityTypeSchema,
        entity_id: z.uuid(),
      })
    )
    .max(100), // Limit to 100 checks per request
});

// Team schemas
export const teamRoleSchema = z.enum(["owner", "member"]);

export const createTeamSchema = z.object({
  name: z
    .string()
    .min(3, "Team name must be at least 3 characters")
    .max(100, "Team name must be less than 100 characters"),
  description: z
    .string()
    .max(500, "Description must be less than 500 characters")
    .optional(),
});

export const updateTeamSchema = z.object({
  name: z
    .string()
    .min(3, "Team name must be at least 3 characters")
    .max(100, "Team name must be less than 100 characters")
    .optional(),
  description: z
    .string()
    .max(500, "Description must be less than 500 characters")
    .optional()
    .nullable(),
});

export const addTeamMemberSchema = z.object({
  userId: z.string().min(1, "User ID is required"),
  role: teamRoleSchema.default("member"),
});

export const updateTeamMemberSchema = z.object({
  role: teamRoleSchema,
});

export const teamSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  description: z.string().nullable(),
  created_by: z.string(),
  created_at: z.iso.datetime(),
  updated_at: z.iso.datetime(),
});

export const teamMemberSchema = z.object({
  id: z.uuid(),
  team_id: z.uuid(),
  user_id: z.string(),
  role: teamRoleSchema,
  joined_at: z.iso.datetime(),
  created_at: z.iso.datetime(),
  handle: z.string().nullable(),
  display_name: z.string().nullable(),
  profile_images: profileImagesSchema.nullable(),
});

export const teamWithMembersSchema = teamSchema.extend({
  members: z.array(teamMemberSchema),
  member_count: z.number().int().min(0),
});

export const teamMemberPreviewSchema = z.object({
  user_id: z.string(),
  handle: z.string().nullable(),
  display_name: z.string().nullable(),
  profile_images: profileImagesSchema.nullable(),
});

export const userTeamSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  role: teamRoleSchema,
  member_count: z.number().int().min(0),
  member_preview: z.array(teamMemberPreviewSchema),
});
